import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { t } from 'i18next';
import axios from '../../config/axios';
import Toast from '../../shared/toast/Toast';
import { ResponseResult } from '../../utils/interfaces/http/ResponseResult';
import {
    CreateOnboardingTasksRequest,
    CreateOnboardingTasksResponse,
    DeleteOnboardingTasksRequest,
    GetOnboardingTasksResponse,
    OnboardingTask,
    OnboardingTasksStoreInterface,
    ReorderOnboardingTasksRequest,
    ReorderOnboardingTasksResponse,
    ToggleQiwaContractRequest,
    ValidateOnboardingTasksRequest,
    ValidateOnboardingTasksResponse,
} from '../../utils/interfaces/onboarding/OnboardingTaskInterfaces';

const appKey = 'onboardingTasks';
const apiModuleUrl = "pa/ft/data-collection-tasks";

// Async Thunks
export const getOnboardingTasks = createAsyncThunk(
    `${appKey}/getOnboardingTasks`,
    async (jobPostExternalId: string) => {
        const response = await axios.get<ResponseResult<GetOnboardingTasksResponse>>(
            `${apiModuleUrl}/job-post/${jobPostExternalId}`
        );
        return response.data.data;
    }
);

export const createOnboardingTasks = createAsyncThunk(
    `${appKey}/createOnboardingTasks`,
    async (data: CreateOnboardingTasksRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<CreateOnboardingTasksResponse>>(
                `${apiModuleUrl}/create`,
                data
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({ 
                message: error?.message || 'Failed to create tasks',
                details: error?.details,
                code: error?.code
            });
        }
    }
);

export const deleteOnboardingTasks = createAsyncThunk(
    `${appKey}/deleteOnboardingTasks`,
    async (data: DeleteOnboardingTasksRequest) => {
        await axios.delete<ResponseResult<null>>(apiModuleUrl, { data });
        return data.taskExternalIds;
    }
);

export const reorderOnboardingTasks = createAsyncThunk(
    `${appKey}/reorderOnboardingTasks`,
    async (data: ReorderOnboardingTasksRequest, { rejectWithValue }) => {
        try {
            const response = await axios.put<ResponseResult<ReorderOnboardingTasksResponse>>(
                `${apiModuleUrl}/reorder`,
                data
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message ?? '' });
        }
    }
);

export const toggleQiwaContract = createAsyncThunk(
    `${appKey}/toggleQiwaContract`,
    async (data: ToggleQiwaContractRequest, { rejectWithValue }) => {
        try {
            await axios.put<ResponseResult<null>>(`${apiModuleUrl}/qiwa-contract`, data);
            return data.includeQiwaContract;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message ?? '' });
        }
    }
);

export const createPredefinedOnboardingTasks = createAsyncThunk(
    `${appKey}/createPredefinedOnboardingTasks`,
    async (params: { includeQiwaContract: boolean } | undefined, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<CreateOnboardingTasksResponse>>(
                `${apiModuleUrl}/predefine`,
                { includeQiwaContract: params?.includeQiwaContract ?? false }
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({
                message: error?.message || 'Failed to create predefined tasks',
                code: error?.code
            });
        }
    }
);

export const validateOnboardingTasks = createAsyncThunk(
    `${appKey}/validateOnboardingTasks`,
    async (data: ValidateOnboardingTasksRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<ValidateOnboardingTasksResponse>>(
                `${apiModuleUrl}/validate`,
                data
            );
            return response.data.data;
        } catch (error: any) {
            // Check if the error has the details directly (custom axios interceptor modified it)
            if (error?.details) {
                const payload = { 
                    message: error?.message || 'Validation failed',
                    status: error?.status || error?.response?.status,
                    details: error?.details,
                    code: error?.code
                };
                return rejectWithValue(payload);
            }
            
            // Fallback to extracting from response.data
            const responseData = error?.response?.data;
            const errorData = responseData?.error || responseData;
            const payload = { 
                message: errorData?.message || error?.message || 'Validation failed',
                status: error?.response?.status,
                details: errorData?.details,
                code: errorData?.code
            };
            return rejectWithValue(payload);
        }
    }
);

const initialState: OnboardingTasksStoreInterface = {
    pendingTasks: [],
    pendingDeleteIds: [],
    showQiwaContract: true,
    originalShowQiwaContract: true,
    isLoadingTasks: false,
    isCreatingTasks: false,
    isCreatingPredefinedTasks: false,
    isDeletingTasks: false,
    isReorderingTasks: false,
    isTogglingQiwaContract: false,
    isValidatingTasks: false,
    validationErrorUids: [],
    validationErrorMessage: '',
    validationErrorMessages: {},
};

export const onboardingTasksSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {
        addPendingTask: (state, action: PayloadAction<OnboardingTask>) => {
            state.pendingTasks.push(action.payload);
        },
        updatePendingTask: (state, action: PayloadAction<OnboardingTask>) => {
            const index = state.pendingTasks.findIndex(t => t.uid === action.payload.uid);
            if (index !== -1) {
                const oldTask = state.pendingTasks[index];
                state.pendingTasks[index] = action.payload;

                if (oldTask.name !== action.payload.name && state.validationErrorUids.includes(action.payload.uid)) {
                    state.validationErrorUids = state.validationErrorUids.filter(uid => uid !== action.payload.uid);
                    delete state.validationErrorMessages[action.payload.uid];
                    
                    if (state.validationErrorUids.length === 0) {
                        state.validationErrorMessage = '';
                    }
                }
            }
        },
        removePendingTask: (state, action: PayloadAction<string>) => {
            state.pendingTasks = state.pendingTasks.filter(t => t.uid !== action.payload);
            
            if (state.validationErrorUids.includes(action.payload)) {
                state.validationErrorUids = state.validationErrorUids.filter(uid => uid !== action.payload);
                delete state.validationErrorMessages[action.payload];
                
                if (state.validationErrorUids.length === 0) {
                    state.validationErrorMessage = '';
                }
            }
        },
        reorderPendingTasks: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
            const [removed] = state.pendingTasks.splice(action.payload.fromIndex, 1);
            state.pendingTasks.splice(action.payload.toIndex, 0, removed);
        },
        setPendingTasks: (state, action: PayloadAction<OnboardingTask[]>) => {
            state.pendingTasks = action.payload;
        },
        addPendingDeleteId: (state, action: PayloadAction<string>) => {
            state.pendingDeleteIds.push(action.payload);
        },
        clearPendingDeleteIds: (state) => {
            state.pendingDeleteIds = [];
        },
        setShowQiwaContract: (state, action: PayloadAction<boolean>) => {
            state.showQiwaContract = action.payload;
        },
        clearOnboardingTasks: (state) => {
            state.pendingTasks = [];
            state.pendingDeleteIds = [];
            state.showQiwaContract = true;
            state.originalShowQiwaContract = true;
            state.validationErrorUids = [];
            state.validationErrorMessage = '';
            state.validationErrorMessages = {};
        },
        setValidationErrorUids: (state, action: PayloadAction<string[]>) => {
            state.validationErrorUids = action.payload;
        },
        setValidationErrorMessages: (state, action: PayloadAction<Record<string, string>>) => {
            state.validationErrorMessages = action.payload;
        },
        clearValidationErrors: (state) => {
            state.validationErrorUids = [];
            state.validationErrorMessage = '';
            state.validationErrorMessages = {};
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Onboarding Tasks
            .addCase(getOnboardingTasks.pending, (state) => {
                state.isLoadingTasks = true;
                state.pendingTasks = [];
            })
            .addCase(getOnboardingTasks.fulfilled, (state, action) => {
                state.isLoadingTasks = false;
                state.showQiwaContract = action.payload.includeQiwaContract;
                state.originalShowQiwaContract = action.payload.includeQiwaContract;
                state.pendingTasks = action.payload.tasks.map((task) => ({
                    uid: crypto.randomUUID(),
                    externalId: task.externalId,
                    type: task.type,
                    name: task.name,
                    additionalInstructions: task.additionalInstructions,
                    isOptional: task.isOptional,
                    file: task.file ?? null,
                    isQiwaRequirement: task.isQiwaRequirement,
                }));
            })
            .addCase(getOnboardingTasks.rejected, (state, action) => {
                state.isLoadingTasks = false;
                Toast.Error({ content: action.error?.message || t('error_loading_onboarding_tasks') });
            })

            // Create Onboarding Tasks
            .addCase(createOnboardingTasks.pending, (state) => {
                state.isCreatingTasks = true;
                state.validationErrorUids = [];
                state.validationErrorMessage = '';
                state.validationErrorMessages = {};
            })
            .addCase(createOnboardingTasks.fulfilled, (state, action) => {
                state.isCreatingTasks = false;
                // Clear validation errors on success
                state.validationErrorUids = [];
                state.validationErrorMessage = '';
                state.validationErrorMessages = {};
                // Assign externalIds to tasks that were just created (match by order in response)
                const created = action.payload.tasks;
                const newTasks = state.pendingTasks.filter(t => !t.externalId);
                newTasks.forEach((task, i) => {
                    if (created[i]?.externalId) {
                        const idx = state.pendingTasks.findIndex(t => t.uid === task.uid);
                        if (idx !== -1) {
                            state.pendingTasks[idx] = { ...state.pendingTasks[idx], externalId: created[i].externalId };
                        }
                    }
                });
            })
            .addCase(createOnboardingTasks.rejected, (state, action) => {
                state.isCreatingTasks = false;
                const payload = action.payload as { details?: Array<{index: number, message: string, code?: string}>; message?: string; code?: string } | undefined;
                
                if (payload?.details?.length) {
                    const newTasks = state.pendingTasks.filter(t => !t.externalId);
                    const errorUids: string[] = [];
                    const errorMessages: Record<string, string> = {};
                    
                    payload.details.forEach(detail => {
                        const taskInNewList = newTasks[detail.index];
                        if (taskInNewList) {
                            errorUids.push(taskInNewList.uid);
                            // Check if it's an MHRSD violation (FT-087 code)
                            const isMHRSD = detail.code === 'FT-087' || detail.message === 'screening_questions_MHRSDGuidelinesViolated';
                            errorMessages[taskInNewList.uid] = isMHRSD ? 'onboarding_tasks_MHRSDGuidelinesViolated' : detail.message;
                        }
                    });
                    
                    // Check if main error is MHRSD violation
                    const isMainMHRSD = payload.code === 'FT-087' || payload.message === 'screening_questions_MHRSDGuidelinesViolated';
                    state.validationErrorMessage = isMainMHRSD ? 'onboarding_tasks_MHRSDGuidelinesViolated' : (payload.message ?? '');
                    state.validationErrorUids = errorUids;
                    state.validationErrorMessages = errorMessages;
                    
                    // Translate the message if it's a known translation key
                    const errorMessage = payload.message === 'screening_questions_MHRSDGuidelinesViolated' 
                        ? t('onboarding_tasks_MHRSDGuidelinesViolated')
                        : (payload.message || t('error_creating_onboarding_tasks'));
                    
                    Toast.Error({ 
                        content: errorMessage
                    });
                } else {
                    Toast.Error({ content: payload?.message || t('error_creating_onboarding_tasks') });
                }
            })

            // Delete Onboarding Tasks
            .addCase(deleteOnboardingTasks.pending, (state) => {
                state.isDeletingTasks = true;
            })
            .addCase(deleteOnboardingTasks.fulfilled, (state, action) => {
                state.isDeletingTasks = false;
                state.pendingTasks = state.pendingTasks.filter(
                    (task) => !action.payload.includes(task.externalId as string)
                );
                state.pendingDeleteIds = [];
            })
            .addCase(deleteOnboardingTasks.rejected, (state, action) => {
                state.isDeletingTasks = false;
                Toast.Error({ content: action.error?.message || t('error_deleting_onboarding_tasks') });
            })

            // Reorder Onboarding Tasks
            .addCase(reorderOnboardingTasks.pending, (state) => {
                state.isReorderingTasks = true;
            })
            .addCase(reorderOnboardingTasks.fulfilled, (state, action) => {
                state.isReorderingTasks = false;
                // Re-sync order from server response while keeping local uid/file state
                const serverTasks = action.payload.tasks;
                state.pendingTasks = serverTasks.map((serverTask) => {
                    const existing = state.pendingTasks.find(t => t.externalId === serverTask.externalId);
                    return existing ?? {
                        uid: crypto.randomUUID(),
                        externalId: serverTask.externalId,
                        type: serverTask.type,
                        name: serverTask.name,
                        additionalInstructions: serverTask.additionalInstructions,
                        isOptional: serverTask.isOptional,
                        file: serverTask.file ?? null,
                        isQiwaRequirement: serverTask.isQiwaRequirement,
                    };
                });
            })
            .addCase(reorderOnboardingTasks.rejected, (state, action) => {
                state.isReorderingTasks = false;
                Toast.Error({ content: action.error?.message || t('error_reordering_onboarding_tasks') });
            })

            // Create Predefined Onboarding Tasks
            .addCase(createPredefinedOnboardingTasks.pending, (state) => {
                state.isCreatingPredefinedTasks = true;
            })
            .addCase(createPredefinedOnboardingTasks.fulfilled, (state, action) => {
                state.isCreatingPredefinedTasks = false;
                const created = action.payload?.tasks ?? [];
                created.forEach(task => {
                    state.pendingTasks.push({
                        uid: crypto.randomUUID(),
                        externalId: task.externalId,
                        type: task.type,
                        name: task.name,
                        additionalInstructions: task.additionalInstructions,
                        isOptional: task.isOptional,
                        file: task.file ?? null,
                        isQiwaRequirement: task.isQiwaRequirement,
                    });
                });
            })
            .addCase(createPredefinedOnboardingTasks.rejected, (state, action) => {
                state.isCreatingPredefinedTasks = false;
                const payload = action.payload as { message?: string } | undefined;
                Toast.Error({ content: payload?.message || t('error_creating_onboarding_tasks') });
            })

            // Toggle Qiwa Contract
            .addCase(toggleQiwaContract.pending, (state) => {
                state.isTogglingQiwaContract = true;
            })
            .addCase(toggleQiwaContract.fulfilled, (state, action) => {
                state.isTogglingQiwaContract = false;
                state.showQiwaContract = action.payload;
                state.originalShowQiwaContract = action.payload;
            })
            .addCase(toggleQiwaContract.rejected, (state, action) => {
                state.isTogglingQiwaContract = false;
                Toast.Error({ content: action.error?.message || t('error_toggling_qiwa_contract') });
            })

            // Validate Onboarding Tasks
            .addCase(validateOnboardingTasks.pending, (state) => {
                state.isValidatingTasks = true;
            })
            .addCase(validateOnboardingTasks.fulfilled, (state) => {
                state.isValidatingTasks = false;
                // Clear any previous validation errors
                state.validationErrorUids = [];
                state.validationErrorMessages = {};
            })
            .addCase(validateOnboardingTasks.rejected, (state) => {
                state.isValidatingTasks = false;
                // Don't show toast for validation errors - let the calling component handle it
            });
    },
});

export const {
    addPendingTask,
    updatePendingTask,
    removePendingTask,
    reorderPendingTasks,
    setPendingTasks,
    addPendingDeleteId,
    clearPendingDeleteIds,
    setShowQiwaContract,
    clearOnboardingTasks,
    setValidationErrorUids,
    setValidationErrorMessages,
    clearValidationErrors,
} = onboardingTasksSlice.actions;

export default onboardingTasksSlice.reducer;
